

import { useState, useRef, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { requestPasswordReset, resetPassword } from '@/lib/api/auth'
import { toast } from 'sonner'

const HoomiLogo = '/assets/hoomi-logo.png'

export default function ForgotPassword() {
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [email, setEmail] = useState('')
  const [otp, setOtp] = useState(['', '', '', '', '', ''])
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [otpCode, setOtpCode] = useState('')
  const [resendTimer, setResendTimer] = useState(60)
  const [canResend, setCanResend] = useState(false)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  useEffect(() => {
    if (step === 2 && resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000)
      return () => clearTimeout(timer)
    } else if (resendTimer === 0) {
      setCanResend(true)
    }
  }, [resendTimer, step])

  const handleOtpChange = (index: number, value: string) => {
    // Only allow numbers
    if (value && !/^\d+$/.test(value)) return

    const newOtp = [...otp]
    newOtp[index] = value.slice(-1) // Only take the last character
    setOtp(newOtp)

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').slice(0, 6)
    if (!/^\d+$/.test(pastedData)) return

    const newOtp = [...otp]
    pastedData.split('').forEach((char, index) => {
      if (index < 6) newOtp[index] = char
    })
    setOtp(newOtp)

    // Focus the next empty input or the last one
    const nextIndex = Math.min(pastedData.length, 5)
    inputRefs.current[nextIndex]?.focus()
  }

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!email.trim()) {
      setError('Please enter your email address')
      return
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      await requestPasswordReset({ email })
      toast.success('OTP sent to your email')
      setStep(2)
      setResendTimer(60)
      setCanResend(false)
      // Focus first OTP input after a short delay
      setTimeout(() => inputRefs.current[0]?.focus(), 100)
    } catch (error: any) {
      console.error('Password reset request error:', error)
      toast.error(error.message || 'Failed to send OTP. Please try again.')
      setError(error.message || 'Failed to send OTP. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleOTPSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const otpCode = otp.join('')

    if (otpCode.length !== 6) {
      toast.error('Please enter all 6 digits')
      return
    }

    setOtpCode(otpCode)
    toast.success('OTP entered!')
    setStep(3)
  }

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!password) {
      setError('Please enter a new password')
      return
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      await resetPassword({
        email,
        otp_code: otpCode,
        password,
        confirm_password: confirmPassword,
      })

      toast.success('Password reset successfully!')
      
      // Redirect to login page after a short delay
      setTimeout(() => {
        navigate('/auth')
      }, 1500)
    } catch (error: any) {
      console.error('Password reset error:', error)
      toast.error(error.message || 'Failed to reset password. Please try again.')
      setError(error.message || 'Failed to reset password. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleResend = async () => {
    if (!canResend) return

    setIsLoading(true)
    try {
      await requestPasswordReset({ email })
      toast.success('OTP sent to your email')
      setResendTimer(60)
      setCanResend(false)
      setOtp(['', '', '', '', '', ''])
      inputRefs.current[0]?.focus()
    } catch (error: any) {
      console.error('Resend OTP error:', error)
      toast.error(error.message || 'Failed to resend OTP')
    } finally {
      setIsLoading(false)
    }
  }

  const isOtpComplete = otp.every(digit => digit !== '')

  return (
    <div className="bg-black flex min-h-svh flex-col items-center justify-center gap-6 p-6 md:p-10 text-white relative">
      {/* Purple gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-black via-purple-950/30 to-black z-0"></div>

      {/* Purple accent elements */}
      <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-purple-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-purple-500/10 rounded-full blur-3xl"></div>

      {/* Header with logo */}
      <div className="absolute top-0 left-0 w-full p-4 flex items-center">
        <Link to="/" className="flex items-center gap-2">
          <img src={HoomiLogo} alt="Hoomi DePIN" width={24} height={24} />
          <span className="font-semibold text-purple-300">Hoomi Social</span>
        </Link>
      </div>

      {/* Form container */}
      <div className="w-full max-w-sm z-10 bg-black/30 backdrop-blur-lg rounded-xl border border-purple-500/20 shadow-xl shadow-purple-500/10 p-6">
        {step === 1 ? (
          // Step 1: Email Input
          <form onSubmit={handleEmailSubmit} className="flex flex-col gap-6">
            <div className="text-center mb-2">
              <h2 className="text-2xl font-bold text-white mb-2">Forgot Password?</h2>
              <p className="text-sm text-white/70">
                Enter your email address and we'll send you an OTP to reset your password
              </p>
            </div>

            {error && (
              <div className="bg-red-500/20 border border-red-500/50 text-white p-3 rounded-md text-sm">
                {error}
              </div>
            )}

            <Input
              type="email"
              placeholder="Email Address"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                setError(null)
              }}
              className="w-full bg-white/90 border-0 text-gray-900 placeholder:text-gray-500 h-12 rounded-lg text-white"
              disabled={isLoading}
            />

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600 text-white h-12 rounded-lg font-semibold"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2"></div>
                  Sending...
                </div>
              ) : (
                'Send OTP'
              )}
            </Button>

            <button
              type="button"
              className="text-white text-sm text-center underline hover:text-purple-300 transition-colors"
              onClick={() => navigate('/auth')}
            >
              Back to Sign In
            </button>
          </form>
        ) : step === 2 ? (
          // Step 2: OTP Input
          <form onSubmit={handleOTPSubmit} className="flex flex-col gap-6">
            <div className="text-center mb-2">
              <h2 className="text-2xl font-bold text-white mb-2">Verify OTP</h2>
              <p className="text-sm text-white/70">
                We have sent a 6 digit OTP to
              </p>
              <p className="text-sm text-white font-semibold mt-1">{email}</p>
            </div>

            {error && (
              <div className="bg-red-500/20 border border-red-500/50 text-white p-3 rounded-md text-sm">
                {error}
              </div>
            )}

            {/* OTP Input boxes */}
            <div className="flex gap-2 justify-center" onPaste={handlePaste}>
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => {
                    inputRefs.current[index] = el
                  }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className="w-12 h-12 text-center text-xl font-semibold bg-white/90 rounded-lg border-0 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all text-gray-900 placeholder:text-gray-500"
                  disabled={isLoading}
                />
              ))}
            </div>

            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setStep(1)
                  setOtp(['', '', '', '', '', ''])
                  setError(null)
                }}
                className="flex-1 bg-white/10 border border-white/30 text-white h-12 rounded-lg hover:bg-white/20 transition-all"
                disabled={isLoading}
              >
                Back
              </Button>

              <Button
                type="submit"
                disabled={!isOtpComplete || isLoading}
                className="flex-1 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600 text-white h-12 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2"></div>
                    Verifying...
                  </div>
                ) : (
                  'Verify'
                )}
              </Button>
            </div>

            {/* Resend OTP */}
            <div className="text-center">
              <button
                type="button"
                onClick={handleResend}
                disabled={!canResend || isLoading}
                className={`text-sm underline transition-colors ${
                  canResend && !isLoading
                    ? 'text-white hover:text-purple-300 cursor-pointer'
                    : 'text-white/50 cursor-not-allowed'
                }`}
              >
                Resend OTP
              </button>
              {!canResend && (
                <p className="text-xs text-white/50 mt-2">
                  You can resend in {resendTimer} seconds
                </p>
              )}
            </div>
          </form>
        ) : (
          // Step 3: New Password
          <form onSubmit={handlePasswordSubmit} className="flex flex-col gap-6">
            <div className="text-center mb-2">
              <h2 className="text-2xl font-bold text-white mb-1">Next, secure</h2>
              <h2 className="text-2xl font-bold text-white">your account</h2>
              <p className="text-sm text-white/70 mt-2">
                Enter your new password
              </p>
            </div>

            {error && (
              <div className="bg-red-500/20 border border-red-500/50 text-white p-3 rounded-md text-sm">
                {error}
              </div>
            )}

            <Input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value)
                setError(null)
              }}
              className="w-full bg-white/90 border-0 text-gray-900 placeholder:text-gray-500 h-12 rounded-lg text-white"
              disabled={isLoading}
            />

            <Input
              type="password"
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value)
                setError(null)
              }}
              className="w-full bg-white/90 border-0 text-gray-900 placeholder:text-gray-500 h-12 rounded-lg text-white"
              disabled={isLoading}
            />

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600 text-white h-12 rounded-lg font-semibold"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2"></div>
                  Resetting...
                </div>
              ) : (
                'Reset Password'
              )}
            </Button>

            <button
              type="button"
              className="text-white text-sm text-center underline hover:text-purple-300 transition-colors"
              onClick={() => navigate('/auth')}
            >
              Back to Sign In
            </button>
          </form>
        )}
      </div>
    </div>
  )
}